import { steps } from "#src/enumerations/step-enumeration.js";
import { useEappStore } from "#src/stores/electronic-application.js";
import { useFlowStore } from "#src/stores/flow.js";
import { useQuotingStore } from "#src/stores/quoting.js";
import { useStepBirthdateStore } from "#src/stores/step-birthdate.js";
import { useStepCompareStore } from "#src/stores/step-compare.js";
import { useStepGenderStore } from "#src/stores/step-gender.js";
import { useStepHealthStore } from "#src/stores/step-health.js";
import { useStepNameStore } from "#src/stores/step-name.js";
import { useStepSmokerStore } from "#src/stores/step-smoker.js";
import { parseErrorMessage } from "#src/util/helpers.js";
import { FlowService } from "#src/services/flow.service.js";

export class FlowPrefillService {
  #pinia;
  #flowService;

  static STEP_MAP = {
    compare: steps.COMPARE,
    quote: steps.QUOTE,
    hearts: steps.HEALTH,
    contact: steps.CONTACT,
    address: steps.ADDRESS,
  };

  constructor(pinia) {
    this.#pinia = pinia;
    this.#flowService = new FlowService(pinia);
  }

  setFromPrefill(prefill, { preCreate = false } = {}) {
    const { step } = this.#normalize(prefill);
    const flow = useFlowStore(this.#pinia);
    this.#validatePrefillStep(step);

    const isPrefillingHearts = step === steps.HEALTH;
    this.#validatePrefillToHearts(isPrefillingHearts);
    if (isPrefillingHearts) {
      flow.step = steps.HEALTH;
      return;
    }

    const isPrefillingCompare = step === steps.COMPARE;
    this.#validatePrefillToCompare(isPrefillingCompare);
    if (isPrefillingCompare) {
      flow.step = steps.COMPARE;
      return;
    }

    this.#validatePrefillToQuote();
    if (step === steps.QUOTE || preCreate) {
      flow.step = steps.QUOTE;
      return;
    }

    this.#validatePrefillToContact();
    if (step === steps.CONTACT) {
      flow.step = steps.CONTACT;
      return;
    }
  }

  async savePrefill(prefill) {
    const eApp = useEappStore(this.#pinia);
    if (!eApp.id || !("step" in prefill)) return;
    const flow = useFlowStore(this.#pinia);

    try {
      await eApp.updateEapp({ step: flow.step });
    } catch (e) {
      throw { step: [parseErrorMessage(e)] };
    }
  }

  #validatePrefillStep(step) {
    const validSteps = Object.keys(FlowPrefillService.STEP_MAP);
    const validStep = Object.values(FlowPrefillService.STEP_MAP).includes(step);
    if (!validStep)
      throw { step: [`Please specify a valid step: ${validSteps.join(", ")}`] };

    return true;
  }

  #validatePrefillToHearts(exact) {
    const nameStep = useStepNameStore(this.#pinia);
    const genderStep = useStepGenderStore(this.#pinia);
    const healthStep = useStepHealthStore(this.#pinia);

    if (!nameStep.inactive) {
      this.#flowService.validateStep(nameStep.generateForm);
    }
    if (!genderStep.inactive) {
      this.#flowService.validateStep(genderStep.generateForm);
    }

    if (exact && healthStep.inactive)
      throw { step: ["Step cannot be hearts for final expense"] };

    return true;
  }

  #validatePrefillToCompare(exact) {
    const healthStep = useStepHealthStore(this.#pinia);
    const smokerStep = useStepSmokerStore(this.#pinia);
    const birthdateStep = useStepBirthdateStore(this.#pinia);
    const compareStep = useStepCompareStore(this.#pinia);

    if (!healthStep.inactive) {
      this.#flowService.validateStep(healthStep.generateForm);
    }

    if (!smokerStep.inactive) {
      this.#flowService.validateStep(smokerStep.generateForm);
    }

    if (!birthdateStep.inactive) {
      this.#flowService.validateStep(birthdateStep.generateForm);
    }

    if (exact && compareStep.inactive) {
      throw { step: ["Compare is unavailable for this configuration"] };
    }

    return true;
  }

  #validatePrefillToQuote() {
    const quoting = useQuotingStore(this.#pinia);
    if (!quoting.params.state) throw { state: ["Please provide a state"] };
    return true;
  }

  #validatePrefillToContact() {
    const eApp = useEappStore(this.#pinia);
    if (!eApp.selectedQuote.id)
      throw { "insured.quote_id": ["Please select a quote"] };
    if (eApp.isDualInsured && !eApp.selectedQuote.joint_insured_quote_id)
      throw { "joint_insured.quote_id": ["Please select a joint quote"] };
  }

  #normalize(params) {
    let step = FlowPrefillService.STEP_MAP[params?.step];
    if (
      !step &&
      Object.values(FlowPrefillService.STEP_MAP).includes(params?.step)
    ) {
      step = params?.step;
    }
    return { step };
  }
}
